import * as React from 'react';

import * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import { TooltipArrow, TooltipContentProps, TooltipPortal } from '@radix-ui/react-tooltip';
import { Control, Controller, ControllerProps, FieldError, FieldPath, FieldValues, FormProvider, PathValue, useFormContext } from 'react-hook-form';

import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

import QuestionIcon from '../icons/question';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';

const Form = FormProvider;

type FormFieldContextValue<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);

const FormField = <TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);

const FormItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div ref={ref} className={cn('flex flex-col gap-1', className)} {...props} />
    </FormItemContext.Provider>
  );
});
FormItem.displayName = 'FormItem';

const FormLabel = React.forwardRef<React.ElementRef<typeof LabelPrimitive.Root>, React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>>(
  ({ className, ...props }, ref) => {
    const { error, formItemId } = useFormField();

    return <Label ref={ref} className={cn(error && 'text-default-red', className)} htmlFor={formItemId} {...props} />;
  },
);
FormLabel.displayName = 'FormLabel';

const FormControl = React.forwardRef<React.ElementRef<typeof Slot>, React.ComponentPropsWithoutRef<typeof Slot>>(({ ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
      aria-invalid={!!error}
      {...props}
    />
  );
});
FormControl.displayName = 'FormControl';

const FormDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField();

  return <p ref={ref} id={formDescriptionId} className={cn('text-secondary-text text-sm', className)} {...props} />;
});
FormDescription.displayName = 'FormDescription';

const FormMessage = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(({ className, children, ...props }, ref) => {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message) : children;

  if (!body) {
    return null;
  }

  return (
    <p ref={ref} id={formMessageId} className={cn('text-default-red text-sm', className)} {...props}>
      {body}
    </p>
  );
});
FormMessage.displayName = 'FormMessage';

function FormFieldItem<T extends FieldValues, N extends FieldPath<T>>({
  label,
  name,
  control,
  required,
  children,
  className,
  labelSize = 'base',
  noErrorMessage = false,
  hideRequiredLabel = false,
}: {
  label?: string;
  name: N;
  control: Control<T>;
  required?: boolean;
  children: (props: { value: PathValue<T, N>; onChange: (value: PathValue<T, N>) => void; error?: FieldError }) => React.ReactNode;
  className?: string;
  labelSize?: 'base' | 'small';
  noErrorMessage?: boolean;
  hideRequiredLabel?: boolean;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <FormItem className={className} id={name}>
          <Label required={required} hideRequiredLabel={hideRequiredLabel} className={cn(labelSize === 'small' && 'text-xs')}>
            {label}
          </Label>
          <FormControl>
            <FormControl>{children({ value, onChange, error })}</FormControl>
          </FormControl>
          {!noErrorMessage && <FormMessage />}
        </FormItem>
      )}
    />
  );
}

function FormFieldItemWithTooltip<T extends FieldValues, N extends FieldPath<T>>({
  label,
  name,
  control,
  required,
  children,
  className,
  labelSize = 'base',
  noErrorMessage = false,
  hideRequiredLabel = false,
  tooltipContent,
  tooltipContentProps,
}: {
  label?: string;
  name: N;
  control: Control<T>;
  required?: boolean;
  children: (props: { value: PathValue<T, N>; onChange: (value: PathValue<T, N>) => void; error?: FieldError }) => React.ReactNode;
  className?: string;
  labelSize?: 'base' | 'small';
  noErrorMessage?: boolean;
  hideRequiredLabel?: boolean;
  tooltipContent?: React.ReactNode;
  tooltipContentProps?: TooltipContentProps;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <FormItem className={className}>
          <div className="flex items-center gap-1">
            <Label required={required} hideRequiredLabel={hideRequiredLabel} className={cn(labelSize === 'small' && 'text-xs')}>
              {label}
            </Label>
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <QuestionIcon />
                </TooltipTrigger>
                <TooltipPortal>
                  <TooltipContent {...tooltipContentProps}>
                    <TooltipArrow className="fill-white drop-shadow-[0_1px_0_rgb(var(--grey-3))]" />
                    {tooltipContent}
                  </TooltipContent>
                </TooltipPortal>
              </Tooltip>
            </TooltipProvider>
          </div>
          <FormControl>
            <FormControl>{children({ value, onChange, error })}</FormControl>
          </FormControl>
          {!noErrorMessage && <FormMessage />}
        </FormItem>
      )}
    />
  );
}

export { useFormField, Form, FormItem, FormLabel, FormControl, FormDescription, FormMessage, FormField, FormFieldItem, FormFieldItemWithTooltip };
